import Search from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { MouseEvent, useState } from 'react';

import { TaxRateSchedule } from '~hooks/useTaxRateSchedule/models';
import useTaxRateSchedule from '~hooks/useTaxRateSchedule/useTaxRateSchedule';

// Tax-schedule metadata behind the magnifier — lets customers verify they
// applied the right schedule. The invoice only snapshots the schedule's
// accounting ID; match it against the company's tax rate schedules to recover
// the schedule name + external id. Each row shows only when present.
export function TaxScheduleBadge({
  accountingId,
  companyId,
}: {
  accountingId: string;
  companyId: string;
}) {
  const theme = useTheme();
  const c = theme.brandV2.colors;
  const { getTaxRateSchedules } = useTaxRateSchedule();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  // undefined = not yet fetched, null = fetched with no match.
  const [matched, setMatched] = useState<TaxRateSchedule | null | undefined>(undefined);

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    if (matched === undefined) {
      getTaxRateSchedules({ companyId })
        .then(({ data }) => {
          setMatched(data.find((s) => s.externalAccountingId === accountingId) ?? null);
        })
        .catch(() => setMatched(null));
    }
  };

  return (
    <>
      <IconButton onClick={handleOpen} size="small" sx={{ p: 0.25, ml: 0.5 }}>
        <Search sx={{ fontSize: 14 }} />
      </IconButton>
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        onClose={() => setAnchorEl(null)}
        open={Boolean(anchorEl)}
      >
        <Box
          sx={{
            p: 1.5,
            minWidth: 220,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.75,
          }}
        >
          <Typography
            sx={{
              fontSize: '9px',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '.06em',
              color: c.treadGray3,
            }}
          >
            {t('approvals.approve_jobs.invoice_equation.tax_schedule')}
          </Typography>
          {matched?.name && (
            <Typography sx={{ fontSize: '14px', fontWeight: 700, color: c.treadBlack }}>
              {matched.name}
            </Typography>
          )}
          <InfoRow
            label={t('approvals.approve_jobs.invoice_equation.accounting_id')}
            value={accountingId}
          />
          {matched?.externalId && (
            <InfoRow
              label={t('approvals.approve_jobs.invoice_equation.external_id')}
              value={matched.externalId}
            />
          )}
        </Box>
      </Popover>
    </>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  const theme = useTheme();
  const c = theme.brandV2.colors;
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
      <Typography sx={{ fontSize: '11px', color: c.treadGray3 }}>{label}</Typography>
      <Typography
        sx={{
          fontSize: '11px',
          fontWeight: 600,
          color: c.treadGray1,
          fontFamily: 'monospace',
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}
